'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import PageLayout from '@/components/layout/PageLayout';
import AuthGuard from '@/components/ui/AuthGuard';
import Card from '@/components/ui/Card';
import ErrorAlert from '@/components/ui/ErrorAlert';
import BackButton from '@/components/ui/BackButton';
import { brokerPlatformApi } from '@/lib/api/brokerPlatformClient';
import { getAvailableBrokers } from '@/config/brokers';
import BrokerSelectionModal from '@/components/broker-platform/BrokerSelectionModal';
import BrokerFormModal from '@/components/broker-platform/BrokerFormModal';
import type { BrokerPlatform, BrokerPlatformCreateRequest } from '@/types/brokerPlatform';
import type { BrokerConfig } from '@/config/brokers';

export default function BrokerPlatformPage() {
  const router = useRouter();
  const [platforms, setPlatforms] = useState<BrokerPlatform[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showBrokerSelection, setShowBrokerSelection] = useState(false);
  const [selectedBroker, setSelectedBroker] = useState<BrokerConfig | null>(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const availableBrokers = getAvailableBrokers();

  const loadPlatforms = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await brokerPlatformApi.getPlatforms();
      if (response.success && response.data) {
        setPlatforms(response.data);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load broker platforms';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPlatforms();
  }, []);

  const handleBrokerSelect = (broker: BrokerConfig) => {
    setSelectedBroker(broker);
    setShowBrokerSelection(false);
    setCreateError(null);
  };

  const handleFormSubmit = async (formData: Record<string, string>) => {
    if (!selectedBroker) {
      setCreateError('No broker selected');
      return;
    }

    setCreating(true);
    setCreateError(null);

    try {
      const transformedData = selectedBroker.transformData
        ? selectedBroker.transformData(formData)
        : {
            broker_name: selectedBroker.name.toLowerCase(),
            ...formData,
          };

      const submitData: BrokerPlatformCreateRequest = {
        broker_name: selectedBroker.name.toLowerCase(),
        ...transformedData,
      };
      await brokerPlatformApi.createPlatform(submitData);
      setSelectedBroker(null);
      await loadPlatforms();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to create broker platform';
      setCreateError(errorMessage);
      throw err;
    } finally {
      setCreating(false);
    }
  };

  const handleCloseModal = () => {
    setSelectedBroker(null);
    setShowBrokerSelection(false);
    setCreateError(null);
  };

  const handleRowClick = (platformId: number) => {
    router.push(`/broker-platform/${platformId}`);
  };

  return (
    <PageLayout>
      <AuthGuard>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 md:py-10">
          {/* Header Section */}
          <header className="mb-8 border-b border-zinc-200 dark:border-zinc-800 pb-6">
            <div className="flex items-center gap-4">
              <BackButton href="/tools" ariaLabel="Back to tools" />
              <div className="hidden sm:flex h-14 w-14 shrink-0 items-center justify-center rounded-md border border-zinc-300 dark:border-zinc-700 text-emerald-700 dark:text-emerald-400">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400">Execution</p>
                <h1 className="mt-2 text-5xl md:text-6xl text-zinc-900 dark:text-white">
                  Broker Platform
                </h1>
                <p className="mt-1 text-sm md:text-base text-zinc-500 dark:text-zinc-400">
                  Connect and manage your broker accounts for automated trading
                </p>
              </div>
            </div>
          </header>

          <main>
            {error && (
              <div className="mb-6 animate-fadeIn">
                <ErrorAlert message={error} />
              </div>
            )}

            {/* Action Bar */}
            <div className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h2 className="text-lg font-bold tracking-tight text-zinc-900 dark:text-white">
                  Connected Brokers
                </h2>
                <p className="text-sm text-zinc-500 dark:text-zinc-400">
                  {platforms.length === 0 
                    ? 'No brokers connected yet' 
                    : `${platforms.length} broker${platforms.length > 1 ? 's' : ''} connected`}
                </p>
              </div>
              <button
                onClick={() => setShowBrokerSelection(true)}
                className="group inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40"
              >
                <svg className="w-5 h-5 group-hover:rotate-90 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span>Add New Broker</span>
              </button>
            </div>

            <Card>
              {loading ? (
                <div className="flex flex-col justify-center items-center py-20">
                  <div className="relative">
                    <div className="w-12 h-12 border-4 border-emerald-200 dark:border-emerald-900 border-t-emerald-600 dark:border-t-emerald-400 rounded-full animate-spin"></div>
                  </div>
                  <p className="mt-4 text-zinc-500 dark:text-zinc-400 font-medium">Loading brokers...</p>
                </div>
              ) : platforms.length === 0 ? (
                <div className="text-center py-16 px-6">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-500/10 mb-6">
                    <svg
                      className="w-10 h-10 text-emerald-600 dark:text-emerald-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                      />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-zinc-900 dark:text-white mb-2">
                    No Brokers Connected
                  </h3>
                  <p className="text-zinc-500 dark:text-zinc-400 mb-6 max-w-md mx-auto">
                    Get started by connecting your first broker platform. This will enable automated trading and order management.
                  </p>
                  <button
                    onClick={() => setShowBrokerSelection(true)}
                    className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-700"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Connect Your First Broker
                  </button>
                </div>
              ) : (
                <div className="-m-6 overflow-hidden rounded-2xl">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-zinc-200 dark:divide-zinc-800">
                      <thead className="bg-zinc-50 dark:bg-zinc-800/60">
                        <tr>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
                            ID
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
                            Broker
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-4 text-right text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-zinc-900 divide-y divide-zinc-200 dark:divide-zinc-800">
                        {platforms.map((platform, index) => (
                          <tr
                            key={platform.id}
                            onClick={() => handleRowClick(platform.id)}
                            className="group hover:bg-zinc-50 dark:hover:bg-zinc-800/50 cursor-pointer transition-colors"
                            style={{ animationDelay: `${index * 50}ms` }}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleRowClick(platform.id);
                              }
                            }}
                            aria-label={`View broker platform ${platform.broker_name}`}
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 font-semibold text-sm">
                                {platform.id}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 capitalize">
                                {platform.broker_name}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                Connected
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right">
                              <div className="flex items-center justify-end gap-2">
                                <span className="text-xs text-zinc-500 dark:text-zinc-400 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">
                                  View Details
                                </span>
                                <svg
                                  className="w-5 h-5 text-zinc-400 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-all duration-200 transform group-hover:translate-x-1"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 5l7 7-7 7"
                                  />
                                </svg>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </Card>
          </main>

          {/* Broker Selection Modal */}
          {showBrokerSelection && !selectedBroker && (
            <BrokerSelectionModal
              brokers={availableBrokers}
              onSelect={handleBrokerSelect}
              onClose={handleCloseModal}
            />
          )}

          {/* Form Modal */}
          {selectedBroker && (
            <BrokerFormModal
              broker={selectedBroker}
              onSubmit={handleFormSubmit}
              onClose={handleCloseModal}
              isSubmitting={creating}
              error={createError}
            />
          )}
        </div>
      </AuthGuard>
    </PageLayout>
  );
}

